import React, { useContext } from "react";
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { useCategories } from '../context/CategoriesContext';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';
import { ProductContext } from "../context/ProductContext";

function ProductCard({ id, image, title, originalPrice, salePrice, category, categoryName, categoryMap, minimumOrderQty = 1 }) {
  const [isHovered, setIsHovered] = useState(false);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  // Card click handler
  const handleCardClick = (e) => {
    // Don't navigate if clicking on the cart button
    if (e.target.closest('button[aria-label*="cart"]')) {
      return;
    }
    navigate(`/product/${id}`);
  };

  // Cart icon click handler - now adds to cart with authentication check
  const handleCartClick = (e) => {
    e.stopPropagation(); // Prevent card click

    const user = JSON.parse(localStorage.getItem("user"));

    if (!user) {
      navigate("/signin");
      return;
    }

    try {
      // Determine the display price (use sale price if available, otherwise original price)
      const displayPrice = salePrice ? salePrice.replace('₹', '') : (originalPrice ? originalPrice.replace('₹', '') : '0');

      // Get category name from categoryName prop or map category ID to name
      const categoryDisplayName = categoryName || categoryMap[String(category)] || category || 'Uncategorized';

      // Add product to cart with category name as categoryId
      addToCart({
        id: id,
        name: title,
        price: displayPrice,
        image: image,
        categoryId: categoryDisplayName, // Send category name instead of ID
        categoryName: categoryDisplayName, // Also set categoryName for consistency
        min_order_qty: minimumOrderQty,
        minimumOrderQty: minimumOrderQty
      }, minimumOrderQty); // Use minimum order quantity

      // Show success toast with quantity information
      toast.success(`${minimumOrderQty > 1 ? `${minimumOrderQty} × ` : ''}${title} added to cart!`, {
        icon: <ShoppingCart size={16} className="text-green-600" />,
        duration: 2000,
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add item to cart');
    }
  };

  // Calculate discount percentage
  const calculateDiscount = () => {
    if (!originalPrice || !salePrice) return null;

    const original = parseFloat(originalPrice.replace('₹', ''));
    const sale = parseFloat(salePrice.replace('₹', ''));

    if (original > sale) {
      const discount = Math.round(((original - sale) / original) * 100);
      return discount;
    }
    return null;
  };

  const discountPercentage = calculateDiscount();

  // Alternative images for hover effect - you can customize these
  const hoverImages = {

  };

  const currentImage = isHovered && hoverImages[image] ? hoverImages[image] : image;

  return (
    <article
      className="flex flex-col h-full p-4 mx-auto w-full bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group"
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="article"
      aria-labelledby={`product-${id}-name`}
    >
      {/* Discount Ribbon */}
      {discountPercentage && (
        <div className="absolute top-0 left-4 z-20 transform translate-y-0">
          <div className="relative">
            {/* Main ribbon body */}
            <div className="relative w-12 bg-gradient-to-b from-red-500 via-red-600 to-red-700 shadow-2xl">
              {/* Top rounded cap */}
              <div className="w-full h-0 bg-gradient-to-b from-red-400 to-red-500 rounded-t-lg"></div>

              {/* Content area */}
              <div className="px-1 py-3 text-center relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="w-full h-full bg-gradient-to-br from-white via-transparent to-transparent"></div>
                </div>

                {/* Percentage number */}
                <div className="relative z-10 text-white font-black text-xl leading-none mb-1 drop-shadow-lg">
                  {discountPercentage}%
                </div>

                {/* OFF text */}
                <div className="relative z-10 text-white font-bold text-xs tracking-wider drop-shadow-md">
                  OFF
                </div>

                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 transform -skew-y-12 animate-shine-vertical"></div>
              </div>

              {/* V-shaped bottom */}
              <div className="relative">
                <div className="w-0 h-0 mx-auto border-l-8 border-r-8 border-t-6 border-l-red-700 border-r-red-700 border-t-red-700"></div>
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-6 border-r-6 border-t-4 border-l-red-800 border-r-red-800 border-t-red-800"></div>
              </div>

              {/* Side shadows for 3D effect */}
              <div className="absolute inset-y-0 -left-1 w-1 bg-gradient-to-b from-red-800 to-red-900 opacity-60 rounded-l-sm"></div>
              <div className="absolute inset-y-0 -right-1 w-1 bg-gradient-to-b from-red-300 to-red-400 opacity-40 rounded-r-sm"></div>
            </div>

            {/* Outer glow */}
            <div className="absolute inset-0 bg-red-500 opacity-30 blur-lg scale-100 rounded-lg"></div>

            {/* Floating sparkles */}
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-300 rounded-full animate-ping opacity-75"></div>
            <div className="absolute top-6 -left-2 w-1.5 h-1.5 bg-yellow-200 rounded-full animate-pulse opacity-60"></div>
          </div>
        </div>
      )}

      {/* Image Container with overlays */}
      <div className="relative overflow-hidden mb-4 rounded-lg">
        <img
          src={currentImage}
          alt={title}
          className="object-contain w-full aspect-[0.85] transition-all duration-500 group-hover:scale-105"
          loading="lazy"
        />

        {/* Cart Icon - Top Right */}
        <button
          onClick={handleCartClick}
          className="absolute top-1 right-1 z-10 w-10 h-10 bg-black hover:bg-gray-800 flex items-center justify-center shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-110 group/cart"
          aria-label={`Add ${minimumOrderQty > 1 ? `${minimumOrderQty} × ` : ''}${title} to cart`}
          title={`Add ${minimumOrderQty} units to cart${minimumOrderQty > 1 ? ' (minimum order quantity)' : ''}`}
        >
          <ShoppingCart
            size={22}
            className="text-white group-hover/cart:text-gray-200 transition-colors duration-200"
          />
          {/* Show minimum quantity badge if greater than 1 */}
          {/* {minimumOrderQty > 1 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
              {minimumOrderQty}
            </span>
          )} */}
        </button>

        {/* Overlay effect on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-300 rounded-lg"></div>
      </div>

      {/* Content Container - Flexible height with consistent spacing */}
      <div className="flex flex-col flex-1 justify-between">
        {/* Title and Price Section - Same line */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3
            id={`product-${id}-name`}
            className="flex-1 text-base font-semibold text-black capitalize group-hover:text-zinc-700 transition-colors duration-200 truncate leading-tight"
            title={title}
          >
            {title}
          </h3>
          <div className="flex items-end gap-2 flex-shrink-0">
            <span
              className={`text-lg font-bold transition-colors duration-200 ${
                originalPrice
                  ? 'text-red-500 group-hover:text-red-600'
                  : 'text-black group-hover:text-zinc-700'
              }`}
              aria-label={`${originalPrice ? 'Sale' : 'Current'} price ${salePrice}`}
            >
              {salePrice}
            </span>
            {originalPrice && (
              <span
                className="text-lg text-gray-500 line-through"
                aria-label={`Original price ${originalPrice}`}
              >
                {originalPrice}
              </span>
            )}
          </div>
        </div>

        {/* Category Section */}
        <div>
          <p className="text-xs text-gray-500 truncate">
            {categoryName || categoryMap[String(category)] || "Uncategorized"}
          </p>
       
          {minimumOrderQty > 1 && (
            <p className="text-xs text-blue-600 font-medium mt-1">
              Min. order: {minimumOrderQty} units
            </p>
          )}
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes shine-vertical {
          0% { transform: translateY(-100%) skewY(-12deg); }
          100% { transform: translateY(200%) skewY(-12deg); }
        }

        .animate-shine-vertical {
          animation: shine-vertical 3s infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }

        .animate-float {
          animation: float 2s ease-in-out infinite;
        }
      `}</style>
    </article>
  );
}

function ProductGrid({ filteredProducts: propFilteredProducts }) {
  const { loading } = useContext(ProductContext);
  const { categories = [] } = useCategories();

  // Create category mapping
  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((c) => {
      map[String(c.id)] = c.title;
    });
    return map;
  }, [categories]);

  // Only use the filtered products passed as props, no fallback
  const displayProducts = (propFilteredProducts || []).slice(0, 4);

  if (loading) {
    return (
      <div className="mt-10 text-center text-gray-600" role="status" aria-live="polite">
        <div className="inline-flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" aria-hidden="true"></div>
          Loading products...
        </div>
      </div>
    );
  }

  if (!Array.isArray(displayProducts) || displayProducts.length === 0) {
    return (
      <div className="mt-10 text-center text-gray-500" role="status" aria-live="polite">
        <div className="flex flex-col items-center gap-4">
          <div className="text-6xl opacity-20" aria-hidden="true">🔍</div>
          <div>
            <h3 className="text-lg font-medium mb-2">No products found</h3>
            <p className="text-sm">Try adjusting your filters or search criteria</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="mt-4 w-full max-w-[1308px] mx-auto px-4" aria-label="Product grid">
      <div className="sr-only" aria-live="polite">
        Showing {displayProducts.length} product{displayProducts.length !== 1 ? 's' : ''}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {displayProducts.map((product) => {
          // Convert prices to numbers and handle edge cases
          const originalPrice = Number(product.price) || 0;
          const offerPrice = Number(product.offerPrice) || 0;

          // Determine if there's a valid offer
          const hasOffer = offerPrice > 0 && offerPrice < originalPrice;

          // Determine the display price (use offer price if available, otherwise original price)
          const displayPrice = hasOffer ? offerPrice : originalPrice;

          return (
            <ProductCard
              key={product.id}
              id={product.id}
              image={product.image || "/placeholder.png"}
              title={product.name}
              originalPrice={hasOffer ? `₹${originalPrice}` : null}
              salePrice={`₹${displayPrice}`}
              category={product.categoryId}
              categoryName={product.categoryName}
              categoryMap={categoryMap}
              minimumOrderQty={product?.minimumOrderQty || product?.min_order_qty || 1}
            />
          );
        })}
      </div>
    </section>
  );
}

export default ProductGrid;
